<template>
  <section class="wizard">
    <div class="wizard__head">
      <h2>Help Me Choose a Platform</h2>
    </div>
    <div class="wizard__body">
      <div class="body__content">
        <div class="wizard__content" id="wizard__content">
          <form-wizard
            shape="circle"
            color="#19557f"
            title="Platform Wizard"
            subtitle=""
            stepSize="xs"
            finishButtonText="Next"

            :validateOnBack="false"
            @on-change="slideChange"
            @on-complete="wizardComplete"
          >

            <!-- [TAB 1] -->

            <tab-content :beforeChange="checkSlide">
              <div
                :class="[
                  'wizard-field',
                  'wizard-field--stacked',
                  { 'has-error': errors ? errors.has('core_benefits') : false }
                ]"
              >
                <h3 class="wizard-question">Do you need support for core benefits?</h3>
                <radio
                  name="core_benefits"
                  v-model="wizardAnswers.core_benefits"
                  :value="true"
                >
                  Yes
                </radio>
                <radio
                  name="core_benefits"
                  v-model="wizardAnswers.core_benefits"
                  :value="false"
                  @click.native.prevent="shortCircuitToHarmony"
                >
                  No
                </radio>
              </div>
            </tab-content>

            <!-- [TAB 2] -->

            <tab-content :beforeChange="checkSlide">
              <h3 class="wizard-question">Which products will you be offering?</h3>

              <!-- Accident Insurance -->
              <div
                :class="[
                  'wizard-field wizard-field--checkbox',
                  { 'has-error': errors ? errors.has('accident_stand_alone') : false }
                ]"
              >
                <checkbox
                  name="accident"
                  v-model="wizardAnswers.products.accident.supported"
                >
                  Accident Insurance
                </checkbox>
              </div>
              <div
                :class="{
                  'wizard-field wizard-field--conditional': true,
                  'wizard-field--disabled': !wizardAnswers.products.accident.supported,
                  'has-error': !slideChangeSuccess && wizardAnswers.products.accident.supported && wizardAnswers.products.accident.standAlonePoliciesSupported === null
                }">
                <div class="wizard-field--conditional__question">
                  Will spouse/child standalone policies be needed?
                </div>
                <div class="wizard-field--radio">
                  <radio
                    name="accident_stand_alone"
                    v-model="wizardAnswers.products.accident.standAlonePoliciesSupported"
                    :value="wizardAnswers.products.accident.supported ? true : null"
                    :disabled="!wizardAnswers.products.accident.supported"
                  >
                    Yes
                  </radio>
                  <radio
                    name="accident_stand_alone"
                    v-model="wizardAnswers.products.accident.standAlonePoliciesSupported"
                    :value="wizardAnswers.products.accident.supported ? false : null"
                    :disabled="!wizardAnswers.products.accident.supported"
                  >
                    No
                  </radio>
                </div>
              </div>

              <!-- Group Accident -->
              <div class="wizard-field wizard-field--checkbox">
                <checkbox
                  name="group_accident"
                  v-model="wizardAnswers.products.group_accident.supported"
                >
                  Group Accident
                </checkbox>
              </div>

              <!-- Cancer Insurance -->
              <div
                :class="[
                  'wizard-field wizard-field--checkbox',
                  { 'has-error': errors ? errors.has('cancer_stand_alone') : false }
                ]"
              >
                <checkbox
                  name="cancer"
                  v-model="wizardAnswers.products.cancer.supported"
                >
                  Cancer Insurance
                </checkbox>
              </div>
              <div
                :class="{
                  'wizard-field wizard-field--conditional': true,
                  'wizard-field--disabled': !wizardAnswers.products.cancer.supported,
                  'has-error': !slideChangeSuccess && wizardAnswers.products.cancer.supported && wizardAnswers.products.cancer.standAlonePoliciesSupported === null
                }">
                <div class="wizard-field--conditional__question">
                  Will spouse/child standalone policies be needed?
                </div>
                <div class="wizard-field--radio">
                  <radio
                    name="cancer_stand_alone"
                    v-model="wizardAnswers.products.cancer.standAlonePoliciesSupported"
                    :value="wizardAnswers.products.cancer.supported ? true : null"
                    :disabled="!wizardAnswers.products.cancer.supported"
                  >
                    Yes
                  </radio>
                  <radio
                    name="cancer_stand_alone"
                    v-model="wizardAnswers.products.cancer.standAlonePoliciesSupported"
                    :value="wizardAnswers.products.cancer.supported ? false : null"
                    :disabled="!wizardAnswers.products.cancer.supported"
                  >
                    No
                  </radio>
                </div>
              </div>

              <!-- Critical Illness Insurance -->
              <div class="wizard-field wizard-field--checkbox">
                <checkbox
                  name="critical_illness"
                  v-model="wizardAnswers.products.critical_illness.supported"
                >
                  Critical Illness Insurance
                </checkbox>
              </div>

              <!-- Group Specified Disease Insurance -->
              <div class="wizard-field wizard-field--checkbox">
                <checkbox
                  name="group_specified_disease"
                  v-model="wizardAnswers.products.group_specified_disease.supported"
                >
                  Group Specified Disease Insurance
                </checkbox>
              </div>

              <!-- Dental Insurance -->
              <div class="wizard-field wizard-field--checkbox">
                <checkbox
                  name="dental"
                  v-model="wizardAnswers.products.dental.supported"
                >
                  Dental Insurance
                </checkbox>
              </div>

              <!-- Disability Insurance -->
              <div class="wizard-field wizard-field--checkbox">
                <checkbox
                  name="disability"
                  v-model="wizardAnswers.products.disability.supported"
                >
                  Disability Insurance
                </checkbox>
              </div>

              <!-- Group Disability Insurance -->
              <div class="wizard-field wizard-field--checkbox">
                <checkbox
                  name="group_disability"
                  v-model="wizardAnswers.products.group_disability.supported"
                >
                  Group Disability Insurance
                </checkbox>
              </div>

              <!-- Hospital Confinement Insurance -->
              <div class="wizard-field wizard-field--checkbox">
                <checkbox
                  name="hospital_confinement"
                  v-model="wizardAnswers.products.hospital_confinement.supported"
                >
                  Hospital Confinement Insurance
                </checkbox>
              </div>

              <!-- Group Hospital Indemnity Insurance -->
              <div class="wizard-field wizard-field--checkbox">
                <checkbox
                  name="group_hospital_indemnity"
                  v-model="wizardAnswers.products.group_hospital_indemnity.supported"
                >
                  Group Hospital Indemnity Insurance
                </checkbox>
              </div>
            </tab-content>

            <!-- [TAB 3] -->

            <tab-content :beforeChange="checkSlide">
              <h3 class="wizard-question">Which products will you be offering? (continued)</h3>

              <!-- Term Life Insurance -->
              <div
                :class="[
                  'wizard-field wizard-field--checkbox',
                  { 'has-error': errors ?
                    errors.has('term_life_stand_alone') ||
                    errors.has('term_life_stacked') : false
                  }
                ]"
              >
                <checkbox
                  name="term_life"
                  v-model="wizardAnswers.products.term_life.supported"
                >
                  Term Life Insurance
                </checkbox>
              </div>
              <div
                :class="{
                  'wizard-field wizard-field--conditional': true,
                  'wizard-field--disabled': !wizardAnswers.products.term_life.supported,
                  'has-error': !slideChangeSuccess && wizardAnswers.products.term_life.supported && wizardAnswers.products.term_life.standAlonePoliciesSupported === null
                }"
              >
                <div class="wizard-field--conditional__question">
                  Will spouse/child standalone policies be needed?
                </div>
                <div class="wizard-field--radio">
                  <radio
                    name="term_life_stand_alone"
                    v-model="wizardAnswers.products.term_life.standAlonePoliciesSupported"
                    :value="wizardAnswers.products.term_life.supported ? true : null"
                    :disabled="!wizardAnswers.products.term_life.supported"
                  >
                    Yes
                  </radio>
                  <radio
                    name="term_life_stand_alone"
                    v-model="wizardAnswers.products.term_life.standAlonePoliciesSupported"
                    :value="wizardAnswers.products.term_life.supported ? false : null"
                    :disabled="!wizardAnswers.products.term_life.supported"
                  >
                    No
                  </radio>
                </div>
              </div>
              <div
                :class="{
                  'wizard-field wizard-field--conditional': true,
                  'wizard-field--disabled': !wizardAnswers.products.term_life.supported,
                  'has-error': !slideChangeSuccess && wizardAnswers.products.term_life.supported && wizardAnswers.products.term_life.stackedCoverageSupported === null
                }">
                <div class="wizard-field--conditional__question">
                  Will stacked coverage be needed?
                </div>
                <div class="wizard-field--radio">
                  <radio
                    name="term_life_stacked"
                    v-model="wizardAnswers.products.term_life.stackedCoverageSupported"
                    :value="wizardAnswers.products.term_life.supported ? true : null"
                    :disabled="!wizardAnswers.products.term_life.supported"
                  >
                    Yes
                  </radio>
                  <radio
                    name="term_life_stacked"
                    v-model="wizardAnswers.products.term_life.stackedCoverageSupported"
                    :value="wizardAnswers.products.term_life.supported ? false : null"
                    :disabled="!wizardAnswers.products.term_life.supported"
                  >
                    No
                  </radio>
                </div>
              </div>

              <!-- Group Term Life -->
              <div class="wizard-field wizard-field--checkbox">
                <checkbox
                  name="group_term_life"
                  v-model="wizardAnswers.products.group_term_life.supported"
                  id="group-term-life-checkbox"
                >
                  Group Term Life
                </checkbox>
              </div>

              <!-- Universal Life Insurance -->
              <div
                :class="[
                  'wizard-field wizard-field--checkbox',
                  { 'has-error': errors ?
                    errors.has('universal_life_stand_alone') ||
                    errors.has('universal_life_stacked') : false
                  }
                ]"
              >
                <checkbox
                  name="universal_life"
                  v-model="wizardAnswers.products.universal_life.supported"
                >
                  Universal Life Insurance
                </checkbox>
              </div>
              <div
                :class="{
                  'wizard-field wizard-field--conditional': true,
                  'wizard-field--disabled': !wizardAnswers.products.universal_life.supported,
                  'has-error': !slideChangeSuccess && wizardAnswers.products.universal_life.supported && wizardAnswers.products.universal_life.standAlonePoliciesSupported === null
                }">
                <div class="wizard-field--conditional__question">
                  Will spouse/child standalone policies be needed?
                </div>
                <div class="wizard-field--radio">
                  <radio
                    name="universal_life_stand_alone"
                    v-model="wizardAnswers.products.universal_life.standAlonePoliciesSupported"
                    :value="wizardAnswers.products.universal_life.supported ? true : null"
                    :disabled="!wizardAnswers.products.universal_life.supported"
                  >
                    Yes
                  </radio>
                  <radio
                    name="universal_life_stand_alone"
                    v-model="wizardAnswers.products.universal_life.standAlonePoliciesSupported"
                    :value="wizardAnswers.products.universal_life.supported ? false : null"
                    :disabled="!wizardAnswers.products.universal_life.supported"
                  >
                    No
                  </radio>
                </div>
              </div>
              <div
                :class="{
                  'wizard-field wizard-field--conditional': true,
                  'wizard-field--disabled': !wizardAnswers.products.universal_life.supported,
                  'has-error': !slideChangeSuccess && wizardAnswers.products.universal_life.supported && wizardAnswers.products.universal_life.stackedCoverageSupported === null
                }">
                <div class="wizard-field--conditional__question">
                  Will stacked coverage be needed?
                </div>
                <div class="wizard-field--radio">
                  <radio
                    name="universal_life_stacked"
                    v-model="wizardAnswers.products.universal_life.stackedCoverageSupported"
                    :value="wizardAnswers.products.universal_life.supported ? true : null"
                    :disabled="!wizardAnswers.products.universal_life.supported"
                  >
                    Yes
                  </radio>
                  <radio
                    name="universal_life_stacked"
                    v-model="wizardAnswers.products.universal_life.stackedCoverageSupported"
                    :value="wizardAnswers.products.universal_life.supported ? false : null"
                    :disabled="!wizardAnswers.products.universal_life.supported"
                  >
                    No
                  </radio>
                </div>
              </div>

              <!-- Whole Life Insurance -->
              <div
                :class="[
                  'wizard-field wizard-field--checkbox',
                  { 'has-error': errors ?
                    errors.has('whole_life_stand_alone') ||
                    errors.has('whole_life_stacked') : false
                  }
                ]"
              >
                <checkbox
                  name="whole_life"
                  v-model="wizardAnswers.products.whole_life.supported"
                >
                  Whole Life Insurance
                </checkbox>
              </div>
              <div
                :class="{
                  'wizard-field wizard-field--conditional': true,
                  'wizard-field--disabled': !wizardAnswers.products.whole_life.supported,
                  'has-error': !slideChangeSuccess && wizardAnswers.products.whole_life.supported && wizardAnswers.products.whole_life.standAlonePoliciesSupported === null
                }">
                <div class="wizard-field--conditional__question">
                  Will spouse/child standalone policies be needed?
                </div>
                <div class="wizard-field--radio">
                  <radio
                    name="whole_life_stand_alone"
                    v-model="wizardAnswers.products.whole_life.standAlonePoliciesSupported"
                    :value="wizardAnswers.products.whole_life.supported ? true : null"
                    :disabled="!wizardAnswers.products.whole_life.supported"
                  >
                    Yes
                  </radio>
                  <radio
                    name="whole_life_stand_alone"
                    v-model="wizardAnswers.products.whole_life.standAlonePoliciesSupported"
                    :value="wizardAnswers.products.whole_life.supported ? false : null"
                    :disabled="!wizardAnswers.products.whole_life.supported"
                  >
                    No
                  </radio>
                </div>
              </div>
              <div
                :class="{
                  'wizard-field wizard-field--conditional': true,
                  'wizard-field--disabled': !wizardAnswers.products.whole_life.supported,
                  'has-error': !slideChangeSuccess && wizardAnswers.products.whole_life.supported && wizardAnswers.products.whole_life.stackedCoverageSupported === null
                }">
                <div
                  :class="[
                    'wizard-field--conditional__question',
                    {'has-error': errors ? errors.has('whole_life_stacked') : false}
                  ]"
                >
                  Will stacked coverage be needed?
                </div>
                <div class="wizard-field--radio">
                  <radio
                    name="whole_life_stacked"
                    v-model="wizardAnswers.products.whole_life.stackedCoverageSupported"
                    :value="wizardAnswers.products.whole_life.supported ? true : null"
                    :disabled="!wizardAnswers.products.whole_life.supported"
                  >
                    Yes
                  </radio>
                  <radio
                    name="whole_life_stacked"
                    v-model="wizardAnswers.products.whole_life.stackedCoverageSupported"
                    :value="wizardAnswers.products.whole_life.supported ? false : null"
                    :disabled="!wizardAnswers.products.whole_life.supported"
                  >
                    No
                  </radio>
                </div>
              </div>

              <!-- QBPC -->
              <div
                :class="{
                  'wizard-field wizard-field--checkbox': true,
                  'has-error': !slideChangeSuccess && wizardAnswers.isQbpcChecked && wizardAnswers.qbpcLimitedOrOther === null
                }"
              >
                <checkbox
                  name="isQbpcChecked"
                  v-model="wizardAnswers.isQbpcChecked"
                >
                  QBPC
                </checkbox>
              </div>
              <div
                :class="[
                  'wizard-field wizard-field--conditional',
                  { 'wizard-field--disabled': !wizardAnswers.isQbpcChecked },
                  { 'has-error': errors ? errors.has('qbpcLimitedOrOther') : false }
                ]">
                <div class="wizard-field--conditional__question wizard-field--stacked">
                  <radio
                    name="qbpcLimitedOrOther"
                    v-model="wizardAnswers.qbpcLimitedOrOther"
                    :value="wizardAnswers.isQbpcChecked
                      ? 'limited'
                      : wizardAnswers.qbpcLimitedOrOther = null"
                    :disabled="!wizardAnswers.isQbpcChecked"
                  >
                    Group Cancer, Group Critical Illness, Banner VSTD, and Banner VGTL ONLY
                  </radio>
                  <radio
                    name="qbpcLimitedOrOther"
                    v-model="wizardAnswers.qbpcLimitedOrOther"
                    :value="wizardAnswers.isQbpcChecked
                      ? 'other'
                      : wizardAnswers.qbpcLimitedOrOther = null"
                    :disabled="!wizardAnswers.isQbpcChecked"
                  >
                    Other Products
                  </radio>
                </div>
              </div>
            </tab-content>

            <!-- [TAB 4] -->

            <tab-content :beforeChange="checkSlide">
              <div class="wizard-quad-layout">

                <!-- Emplployer Paid Products -->
                <div
                  :class="{
                    'wizard-field wizard-field--checkbox': true,
                    'has-error': !slideChangeSuccess && wizardAnswers.employerPaidSupport === null
                  }"
                >
                  <h3 class="wizard-question">Will there be employer paid products?</h3>
                  <div class="wizard-field--conditional__question wizard-field--stacked">
                    <radio
                      name="employerPaidSupport"
                      v-model="wizardAnswers.employerPaidSupport"
                      :value="true"
                      id="employer-paid-support-yes-radio"
                    >
                      Yes
                    </radio>
                    <radio
                      name="employerPaidSupport"
                      v-model="wizardAnswers.employerPaidSupport"
                      :value="false"
                    >
                      No
                    </radio>
                  </div>
                </div>

                <!-- Employee 75% Seen or Spoken to -->
                <div
                  :class="{
                    'wizard-field wizard-field--checkbox': true,
                    'has-error': !slideChangeSuccess && wizardAnswers.employee_75_seen_spoken === null
                  }"
                >
                  <h3 class="wizard-question">Will 75% of your employees be  seen or spoken to?</h3>
                  <div class="wizard-field--conditional__question wizard-field--stacked">
                    <radio
                      name="employee_75_seen_spoken"
                      v-model="wizardAnswers.employee_75_seen_spoken"
                      :value="true"
                      id="employee-75-seen-spoken-yes-radio"
                    >
                      Yes
                    </radio>
                    <radio
                      name="employee_75_seen_spoken"
                      v-model="wizardAnswers.employee_75_seen_spoken"
                      :value="false"
                    >
                      No
                    </radio>
                  </div>
                </div>

                <!-- How do you plan to enroll -->
                <div
                  :class="{
                    'wizard-field wizard-field--checkbox': true,
                    'has-error':
                      !slideChangeSuccess &&
                      wizardAnswers.features.enrollment_agent_assisted.available !== true &&
                      wizardAnswers.features.enrollment_self_enroll.available !== true &&
                      wizardAnswers.features.enrollment_call_center.available !== true &&
                      wizardAnswers.features.auto_census_enroll.available !== true
                  }"
                >
                  <h3 class="wizard-question">How do you plan to enroll?</h3>
                  <p><span class="text-help">Please check all that apply</span></p>
                  <div class="wizard-field--conditional__question wizard-field--flex">
                    <checkbox
                      name="enrollment_agent_assisted"
                      v-model="wizardAnswers.features.enrollment_agent_assisted.available"
                      id="enrollment-agent-assisted-checkbox"
                    >
                      Agent Assisted
                    </checkbox>

                    <checkbox
                      name="enrollment_self_enroll"
                      v-model="wizardAnswers.features.enrollment_self_enroll.available"
                    >
                      Self Enroll
                      <sup v-if="this.hasNonStandardProducts">*</sup>
                    </checkbox>

                    <checkbox
                      name="enrollment_call_center"
                      v-model="wizardAnswers.features.enrollment_call_center.available"
                    >
                      Call Center
                    </checkbox>

                    <checkbox
                      name="auto_census_enroll"
                      v-model="wizardAnswers.features.auto_census_enroll.available"
                    >
                      Census Enroll
                    </checkbox>
                  </div>

                  <div
                    v-if="this.hasNonStandardProducts"
                    class="wizard__help-text"
                  >
                    <em v-if="this.hasNonStandardProducts && this.hasStandardProducts">
                      *Note: Self Enroll is not available for Universal Life or QBPC.
                    </em>
                    <em v-if="this.hasNonStandardProducts && !this.hasStandardProducts">
                      *Note: Self Enroll is not an option when Universal Life or QBPC are selected.
                    </em>

                    <em
                      v-if="(this.hasNonStandardProducts && this.hasStandardProducts) &&
                        (this.wizardAnswers.features.enrollment_agent_assisted.available !== true &&
                        this.wizardAnswers.features.enrollment_call_center.available !== true)"
                      class="has-error"
                    >
                      Selection of <strong>Agent Assisted</strong> and/or
                      <strong>Call Center</strong> is required.
                    </em>
                  </div>
                </div>

                <!-- Who will be building the case? -->
                <div
                  :class="{
                    'wizard-field wizard-field--checkbox': true,
                    'has-error': !slideChangeSuccess && wizardAnswers.buildCaseInternal !== true && wizardAnswers.buildCaseExternal !== true
                  }"
                >
                  <h3 class="wizard-question">Who will be building the case?</h3>
                  <p><span class="text-help">Please check all that apply</span></p>
                  <div class="wizard-field--conditional__question wizard-field--stacked">
                    <checkbox
                      name="buildCaseInternal"
                      v-model="wizardAnswers.buildCaseInternal"
                      id="build-case-internal-checkbox"
                    >
                      Broker or Coordinator
                    </checkbox>
                    <checkbox
                      name="buildCaseExternal"
                      v-model="wizardAnswers.buildCaseExternal"
                    >
                      System or Vendor
                    </checkbox>
                  </div>
                </div>
              </div>
            </tab-content>
            <div v-if="!validated && !slideChangeSuccess" class="errorSummary">
              <p v-if="nextSlideIndex === null || nextSlideIndex === 0">
                Please make a selection.
              </p>
              <p v-if="nextSlideIndex === 2">
                Please make at least one product selection.
              </p>
            </div>
            <template slot="footer" slot-scope="props">
              <div class="wizard-footer-left">
                <button
                  v-if="props.activeTabIndex > 0"
                  @click="props.prevTab()"
                  class="wizard-btn"
                  :style="props.fillButtonStyle"
                  id="wizard-previous-button"
                >
                  Previous
                </button>
              </div>
              <div class="wizard-footer-right">
                <button
                  @click="props.nextTab()"
                  class="wizard-btn"
                  :style="props.fillButtonStyle"
                  id="wizard-next-button"
                >
                  {{ props.isLastStep ? 'Done' : 'Next' }}
                </button>
              </div>
            </template>
          </form-wizard>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { Checkbox, Radio } from 'vue-checkbox-radio'
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'

import UnumIcon from '@/components/ui/Icon'

import sessionEvent from '@/util/sessionEvent'

export default {
  components: {
    FormWizard,
    TabContent,
    Checkbox,
    Radio,
    UnumIcon
  },
  data () {
    return {
      validated: true,
      slideChangeSuccess: true,
      nextSlideIndex: null,
      wizardAnswers: {
        isQbpcChecked: false,
        qbpcLimitedOrOther: null,

        core_benefits: true,
        employerPaidSupport: null,
        employee_75_seen_spoken: null,

        buildCaseColonialLifeOffice: null,
        buildCaseBrokerIntegratedPartner: null,

        // not used by filter, used by wizard to set
        // filter values for the previous two properties,
        // `buildCaseColonialLifeOffice` & `buildCaseBrokerIntegratedPartner`
        buildCaseInternal: null,
        buildCaseExternal: null,

        features: {
          enrollment_agent_assisted: {
            available: null
          },
          enrollment_call_center: {
            available: null
          },
          enrollment_self_enroll: {
            available: null
          },
          auto_census_enroll: {
            available: null
          }
        },

        products: {
          accident: {
            supported: null,
            standAlonePoliciesSupported: null
          },
          group_accident: {
            supported: null
          },
          cancer: {
            supported: null,
            standAlonePoliciesSupported: null
          },
          critical_illness: {
            supported: null
          },
          group_specified_disease: {
            supported: null
          },
          dental: {
            supported: null
          },
          disability: {
            supported: null
          },
          group_disability: {
            supported: null
          },
          hospital_confinement: {
            supported: null
          },
          group_hospital_indemnity: {
            supported: null
          },
          term_life: {
            supported: null,
            standAlonePoliciesSupported: null,
            stackedCoverageSupported: null
          },
          group_term_life: {
            supported: null
          },
          universal_life: {
            supported: null,
            standAlonePoliciesSupported: null,
            stackedCoverageSupported: null
          },
          whole_life: {
            supported: null,
            standAlonePoliciesSupported: null,
            stackedCoverageSupported: null
          },
          qbpc_limited: {
            supported: null
          },
          qbpc_other: {
            supported: null
          }
        }
      }
    }
  },
  async created () {
    if (await this.wizardAnswersFromStore !== null) {
      this.wizardAnswers = Object.assign({}, this.wizardAnswersFromStore)
    }
  },
  mounted () {
    this.clearCurrentPlatform()
    this.clearChosenPlatforms()
  },
  computed: {
    ...mapState('platforms', [
      'platforms'
    ]),
    ...mapState('wizard', {
      wizardAnswersFromStore: 'wizardAnswers'
    }),
    hasSelection_enrollmentPlans () {
      if (this.hasNonStandardProducts && this.hasStandardProducts) {
        const selectedPlans = Object.entries(this.wizardAnswers.features).filter(plan => {
          if (plan[0] === 'enrollment_agent_assisted' || plan[0] === 'enrollment_call_center') {
            return !!plan[1].available
          } else {
            return false
          }
        })

        return selectedPlans.length > 0
      } else {
        return !!this.wizardAnswers.features.enrollment_agent_assisted.available !== false ||
          !!this.wizardAnswers.features.enrollment_self_enroll.available !== false ||
          !!this.wizardAnswers.features.enrollment_call_center.available !== false ||
          !!this.wizardAnswers.features.auto_census_enroll.available !== false
      }
    },
    isOneSelected_caseBuilding () {
      return !!this.wizardAnswers.buildCaseInternal !== false ||
        !!this.wizardAnswers.buildCaseExternal !== false
    },
    hasNonStandardProducts () {
      return this.wizardAnswers.isQbpcChecked || !!this.wizardAnswers.products.universal_life.supported
    },
    hasStandardProducts () {
      const standardProducts = Object.entries(this.wizardAnswers.products).filter(product => {
        if (
          product[0] !== 'qbpc_limited' &&
          product[0] !== 'qbpc_other' &&
          product[0] !== 'universal_life'
        ) {
          return product[1].supported
        }
      })

      return standardProducts.length > 0
    },
    atLeastOneProduct () {
      const products = Object.entries(this.wizardAnswers.products).filter(product => {
        return !!product[1].supported
      })

      return products.length > 0
    }
  },
  methods: {
    ...mapActions('platforms', [
      'platformSelection',
      'clearChosenPlatforms',
      'clearCurrentPlatform'
    ]),
    ...mapActions('wizard', [
      'setWizardAnswers',
      'loadPlatforms',
      'clearWizardAnswers',
      'clearFilteredPlatforms',
      'clearWizard'
    ]),
    lookupPlatform (platformKey) {
      return this.platforms.find(platform => platform['.key'] === platformKey)
    },
    shortCircuitToHarmony () {
      // this.selectPlatform('')
      const harmonyPlatform = this.lookupPlatform('-L5VA0v_9CbsmXeAjI00')
      const sessionKey = this.$storage.get('session_key')
      sessionEvent(sessionKey, 'changed_wizard_field', {
        field: 'Do you need support for core benefits?',
        value: false
      })
      this.platformSelection(harmonyPlatform)
        .then(res => {
          this.$router.push({ name: 'FinalSelection' })
        })
    },
    checkSlide () {
      this.validated = true
      /**
       * Slide 1 - Do you need support for core benefits?
       */
      if (this.nextSlideIndex === null || this.nextSlideIndex === 0) {
        // If 'Do you need support for core benefits?' is unanswered, then invalidate
        if (this.wizardAnswers.core_benefits === null) {
          this.validated = false
        }
      }
      /**
       * Slide 2 - Which products will you be offering?
       */
      if (this.nextSlideIndex === 1) {
        // If 'Accident Insurance' is checked, but its children are unanswered, then invalidate
        if (
          this.wizardAnswers.products.accident.supported &&
          this.wizardAnswers.products.accident.standAlonePoliciesSupported === null
        ) {
          this.validated = false
        }
        // If 'Cancer Insurance' is checked, but its children are unanswered, then invalidate
        if (
          this.wizardAnswers.products.cancer.supported &&
          this.wizardAnswers.products.cancer.standAlonePoliciesSupported === null
        ) {
          this.validated = false
        }
      }
      /**
       * Slide 3 - Which products will you be offering? (continued)
       */
      if (this.nextSlideIndex === 2) {
        // If no products are checked from slides 2 or 3, then invalidate
        if (
          !this.wizardAnswers.products.accident.supported &&
          !this.wizardAnswers.products.group_accident.supported &&
          !this.wizardAnswers.products.cancer.supported &&
          !this.wizardAnswers.products.critical_illness.supported &&
          !this.wizardAnswers.products.group_specified_disease.supported &&
          !this.wizardAnswers.products.dental.supported &&
          !this.wizardAnswers.products.disability.supported &&
          !this.wizardAnswers.products.group_disability.supported &&
          !this.wizardAnswers.products.hospital_confinement.supported &&
          !this.wizardAnswers.products.group_hospital_indemnity.supported &&
          !this.wizardAnswers.products.term_life.supported &&
          !this.wizardAnswers.products.group_term_life.supported &&
          !this.wizardAnswers.products.universal_life.supported &&
          !this.wizardAnswers.products.whole_life.supported &&
          !this.wizardAnswers.isQbpcChecked
        ) {
          this.validated = false
        }
        // If 'Term Life Insurance' is checked, but its children are unanswered, then invalidate
        if (
          this.wizardAnswers.products.term_life.supported &&
          (this.wizardAnswers.products.term_life.standAlonePoliciesSupported === null ||
          this.wizardAnswers.products.term_life.stackedCoverageSupported === null)
        ) {
          this.validated = false
        }
        // If 'Universal Life Insurance' is checked, but its children are unanswered, then invalidate
        if (
          this.wizardAnswers.products.universal_life.supported &&
          (this.wizardAnswers.products.universal_life.standAlonePoliciesSupported === null ||
          this.wizardAnswers.products.universal_life.stackedCoverageSupported === null)
        ) {
          this.validated = false
        }
        // If 'Whole Life Insurance' is checked, but its children are unanswered, then invalidate
        if (
          this.wizardAnswers.products.whole_life.supported &&
          (this.wizardAnswers.products.whole_life.standAlonePoliciesSupported === null ||
          this.wizardAnswers.products.whole_life.stackedCoverageSupported === null)
        ) {
          this.validated = false
        }
        // If 'QBPC' is checked, but its children are unanswered, then invalidate
        if (
          this.wizardAnswers.isQbpcChecked &&
          this.wizardAnswers.qbpcLimitedOrOther === null
        ) {
          this.validated = false
        }
      }
      /**
       * Slide 4 - Misc
       */
      if (this.nextSlideIndex === 3) {
        // If any of the questions are unanswered, then invalidate
        if (
          this.wizardAnswers.employerPaidSupport === null ||
          this.wizardAnswers.employee_75_seen_spoken === null ||
          (this.wizardAnswers.features.enrollment_agent_assisted.available !== true &&
          this.wizardAnswers.features.enrollment_self_enroll.available !== true &&
          this.wizardAnswers.features.enrollment_call_center.available !== true &&
          this.wizardAnswers.features.auto_census_enroll.available !== true) ||
          (this.wizardAnswers.buildCaseInternal !== true &&
          this.wizardAnswers.buildCaseExternal !== true)
        ) {
          this.validated = false
        }
        if (
          (this.hasNonStandardProducts && this.hasStandardProducts) &&
          (this.wizardAnswers.features.enrollment_agent_assisted.available !== true &&
          this.wizardAnswers.features.enrollment_call_center.available !== true)
        ) {
          this.validated = false
        }
      }
      if (!this.validated) {
        this.slideChangeSuccess = false
      }
      return this.validated
    },
    slideChange (prevIndex, nextIndex) {
      this.nextSlideIndex = nextIndex
      this.slideChangeSuccess = true
      const sessionKey = this.$storage.get('session_key')
      if (nextIndex !== -1) {
        sessionEvent(sessionKey, 'clicked_wizard_nav_button', {
          toSlide: nextIndex
        })
      }
      // scroll to top on each slide transition (mainly for mobile)
      document.getElementById('wizard__content').scrollIntoView()
    },
    /* Old validation
    validateNow () {
      return new Promise((resolve, reject) => {
        this.$validator.validate()
          .then(res => {
            // next step button on 3rd slide
            if (this.nextSlideIndex === 2) {
              if (this.hasNonStandardProducts) {
                this.wizardAnswers.features.enrollment_self_enroll.available = null
              }
              if (!this.hasNonStandardProducts && !this.hasStandardProducts) {
                if (!this.errors.has('atLeastOneProduct')) {
                  this.errors.add('atLeastOneProduct', 'Please make at least one product selection')
                }
                reject(res)
              } else {
                this.errors.remove('atLeastOneProduct')
              }
            }
            if (this.errors.items.length) {
              console.log(this.errors.items)
            }
            resolve(res)
          })
          .catch(err => {
            console.error(err)
            reject(err)
          })
      })
    },
    */
    disableSelfEnroll () {
      this.wizardAnswers.features.enrollment_self_enroll.available = false
    },
    wizardComplete () {
      if (this.wizardAnswers.isQbpcChecked) {
        switch (this.wizardAnswers.qbpcLimitedOrOther) {
          case 'limited':
            this.wizardAnswers.products.qbpc_limited.supported = true
            this.wizardAnswers.products.qbpc_other.supported = null
            break
          case 'other':
            this.wizardAnswers.products.qbpc_limited.supported = null
            this.wizardAnswers.products.qbpc_other.supported = true
            break
          default:
            break
        }
      } else {
        this.wizardAnswers.products.qbpc_limited.supported = null
        this.wizardAnswers.products.qbpc_other.supported = null
      }

      // if selected both, then make both unrequired for Broker OR System results
      if (!!this.wizardAnswers.buildCaseExternal &&
        !!this.wizardAnswers.buildCaseInternal) {
        this.wizardAnswers.buildCaseBrokerIntegratedPartner = null
        this.wizardAnswers.buildCaseColonialLifeOffice = null
      } else {
        this.wizardAnswers.buildCaseExternal
          ? this.wizardAnswers.buildCaseBrokerIntegratedPartner = true
          : this.wizardAnswers.buildCaseBrokerIntegratedPartner = null

        this.wizardAnswers.buildCaseInternal
          ? this.wizardAnswers.buildCaseColonialLifeOffice = true
          : this.wizardAnswers.buildCaseColonialLifeOffice = null
      }

      this.loadPlatforms(this.platforms)
      this.setWizardAnswers(this.wizardAnswers)
        .then(res => {
          this.$router.push({ name: 'WizardSummary' })
        })
    }
  },
  watch: {
    'wizardAnswers.core_benefits': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Do you need support for core benefits?',
          field: 'core_benefits',
          value: val
        })
      }
    },
    'wizardAnswers.products.accident.supported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering?',
          field: 'accident_insurance',
          value: val
        })
      }
    },
    'wizardAnswers.products.accident.standAlonePoliciesSupported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering?',
          field: 'accident_insurance_standalone_policies',
          value: val
        })
      }
    },
    'wizardAnswers.products.group_accident.supported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering?',
          field: 'group_accident_insurance',
          value: val
        })
      }
    },
    'wizardAnswers.products.cancer.supported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering?',
          field: 'cancer_insurance',
          value: val
        })
      }
    },
    'wizardAnswers.products.cancer.standAlonePoliciesSupported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering?',
          field: 'cancer_insurance_standalone_policies',
          value: val
        })
      }
    },
    'wizardAnswers.products.critical_illness.supported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering?',
          field: 'critical_illness_insurance',
          value: val
        })
      }
    },
    'wizardAnswers.products.group_specified_disease.supported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering?',
          field: 'group_specified_disease_insurance',
          value: val
        })
      }
    },
    'wizardAnswers.products.dental.supported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering?',
          field: 'dental_insurance',
          value: val
        })
      }
    },
    'wizardAnswers.products.disability.supported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering?',
          field: 'disability_insurance',
          value: val
        })
      }
    },
    'wizardAnswers.products.group_disability.supported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering?',
          field: 'group_disability_insurance',
          value: val
        })
      }
    },
    'wizardAnswers.products.hospital_confinement.supported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering?',
          field: 'hospital_confinement_insurance',
          value: val
        })
      }
    },
    'wizardAnswers.products.group_hospital_indemnity.supported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering?',
          field: 'group_hospital_indemnity_insurance',
          value: val
        })
      }
    },
    'wizardAnswers.products.term_life.supported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering? (continued)',
          field: 'term_life_insurance',
          value: val
        })
      }
    },
    'wizardAnswers.products.term_life.standAlonePoliciesSupported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering? (continued)',
          field: 'term_life_insurance_standalone_policies',
          value: val
        })
      }
    },
    'wizardAnswers.products.term_life.stackedCoverageSupported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering? (continued)',
          field: 'term_life_insurance_stacked_coverage',
          value: val
        })
      }
    },
    'wizardAnswers.products.group_term_life.supported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering? (continued)',
          field: 'group_term_life_insurance',
          value: val
        })
      }
    },
    'wizardAnswers.products.universal_life.supported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering? (continued)',
          field: 'universal_life_insurance',
          value: val
        })
      }
    },
    'wizardAnswers.products.universal_life.standAlonePoliciesSupported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering? (continued)',
          field: 'universal_life_insurance_standalone_policies',
          value: val
        })
      }
    },
    'wizardAnswers.products.universal_life.stackedCoverageSupported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering? (continued)',
          field: 'universal_life_insurance_stacked_coverage',
          value: val
        })
      }
    },
    'wizardAnswers.products.whole_life.supported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering? (continued)',
          field: 'whole_life_insurance',
          value: val
        })
      }
    },
    'wizardAnswers.products.whole_life.standAlonePoliciesSupported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering? (continued)',
          field: 'whole_life_insurance_standalone_policies',
          value: val
        })
      }
    },
    'wizardAnswers.products.whole_life.stackedCoverageSupported': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering? (continued)',
          field: 'whole_life_insurance_stacked_coverage',
          value: val
        })
      }
    },
    'wizardAnswers.isQbpcChecked': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering? (continued)',
          field: 'qbpc',
          value: val
        })
      }
    },
    'wizardAnswers.qbpcLimitedOrOther': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Which products will you be offering? (continued)',
          field: 'qbpc_type',
          value: val
        })
      }
    },
    'wizardAnswers.employerPaidSupport': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Will there be employer paid products?',
          field: 'employer_paid_support',
          value: val
        })
      }
    },
    'wizardAnswers.employee_75_seen_spoken': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Will 75% of your employees be seen or spoken to?',
          field: 'employee_75_seen_spoken',
          value: val
        })
      }
    },
    'wizardAnswers.features.enrollment_agent_assisted.available': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'How do you plan to enroll?',
          field: 'agent_assisted',
          value: val
        })
      }
    },
    'wizardAnswers.features.enrollment_self_enroll.available': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'How do you plan to enroll?',
          field: 'self_enroll',
          value: val
        })
      }
    },
    'wizardAnswers.features.enrollment_call_center.available': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'How do you plan to enroll?',
          field: 'call_center',
          value: val
        })
      }
    },
    'wizardAnswers.features.auto_census_enroll.available': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'How do you plan to enroll?',
          field: 'census_enroll',
          value: val
        })
      }
    },
    'wizardAnswers.buildCaseInternal': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Who will be building the case?',
          field: 'broker_coordinator',
          value: val
        })
      }
    },
    'wizardAnswers.buildCaseExternal': {
      handler: function (val, oldVal) {
        const sessionKey = this.$storage.get('session_key')
        sessionEvent(sessionKey, 'changed_wizard_field', {
          question: 'Who will be building the case?',
          field: 'system_vendor',
          value: val
        })
      }
    }
  },
  beforeRouteLeave (to, from, next) {
    if (to.name !== 'WizardSummary' && to.name !== 'FinalSelection') {
      this.$modal.show('dialog', {
        title: 'Caution',
        text: `By navigating away, you will lose any selections or results so far.`,
        buttons: [
          {
            title: 'Cancel',
            handler: () => {
              this.$modal.hide('dialog')
            }
          },
          {
            title: 'Ok',
            handler: () => {
              this.clearWizard()
              this.clearCurrentPlatform()
              this.clearChosenPlatforms()
              this.$modal.hide('dialog')
              next()
            }
          }
        ]
      })
    } else {
      next()
    }
  }
}

</script>

<style lang="scss">
.iphone-safari {
  .wizard {
    &__content {
      padding: 0 0 44px;
      @include bp($medium) {
        padding: 0;
      }
    }
  }
}
.wizard {
  // background: $lightBlue;
  background: #fff;
  height: calc(100% - 8rem);

  @include bp($medium) {
    height: calc(100% - 5rem);
  }

  &__head {
    // height: 8rem;
    height: 4.688rem;
    background: $lightBlue;
    padding: .75rem 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.25);
    position: relative;
    z-index: 1;

    @include bp($small) {
      padding: .75rem 3rem;
    }

    h2 {
      color: $blue;
      font-weight: 600;
    }
  }

  &__body {
    height: calc(100% - 4.688rem);
    overflow: auto;
  }

  .body {
    &__content {
      height: 100%;
    }
  }

  &__content {
    max-width: 64rem;
    width: 86%;
    margin: 0 auto;
  }

  &-title {
    display: none;
  }

  &-question {
    font-size: 1.375rem;

    & + p {
      margin-top: -1rem;
      margin-bottom: 1.375rem;
    }
  }

  &-field {
    margin: 0 0 1.5rem;
    max-width: 43.750rem;
    @include bp($medium) {
      margin: 0 0 1rem;
    }

    * {
      font-size: 1.5rem;

      @include bp($medium) {
        @include bp($large, 'max-width') {
          font-size: 1.25rem;
        }
      }
      @include bp(1025px) {
        font-size: 1rem;
      }
    }

    &.has-error {
      label {
        color: $red;
      }
      .wizard-question {
        color: $red;
      }
    }

    &--disabled {
      opacity: .25;
    }

    &--conditional {
      display: flex;
      justify-content: space-between;
      padding-left: 1.25rem;

      &.has-error {
        label {
          color: $red;
        }
        .wizard-field--conditional__question {
          color: $red;
        }
      }
    }

    &--radio {
      display: flex;
      justify-content: space-between;
      width: 8rem;
      flex: 0 0 auto;
    }

    &--stacked {
      display: block;

      .radio-component,
      .checkbox-component {
        margin-bottom: 1rem;
        @include bp($medium) {
          margin-bottom: .5rem;
        }
      }
    }

    &--flex {
      display: flex;
      flex-flow: row wrap;
      justify-content: space-between;

      .checkbox-component {
        flex: 0 0 auto;
        // width: calc(50% - .5rem);
        width: 50%;
        margin: 0 auto .5rem;
      }
    }

    label {
      cursor: pointer;
    }
  }

  &-quad-layout {
    @include bp($small) {
      display: flex;
      justify-content: space-between;
      flex-flow: row wrap;
    }

    .wizard-field {
      // width: 375px;
      @include bp($small) {
        width: calc(50% - 2rem);
      }
    }
  }

  &__help-text {
    font-size: .75rem;

    .has-error {
      display: block;
      color: $red;
    }
  }

  // 3rd party override
  .vue-form-wizard {
    position: relative;
    height: calc(100% - 4rem);

    .wizard-tab-content {
      padding: 0;
    }

    .wizard-card-footer {
      margin: 2rem auto 0;
      flex-flow: row nowrap;

      .wizard-footer-left {
        justify-content: flex-start;
      }
      .wizard-footer-right {
        justify-content: flex-end;
        .wizard-btn {
          margin: 0 !important;
        }
      }
    }

    .wizard-btn {
      min-width: auto;
      font-family: $barlow;
      font-size: .875rem;
      padding: .25rem 1rem;
      text-transform: uppercase;

      @include bp($medium, 'max-width') {
        font-size: 1.25rem;
      }
    }

    .wizard-icon-circle {
      &.checked {
        background-color: $blue;
        color: #fff;
      }
    }
  }

  .wizard-tab-container {
    animation: wizardFade .5s ease-in-out;
  }
}

.errorSummary {
  color: $red;
  text-align: right;
}

.checkbox-component {
  .icon {
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
  }
}

@keyframes wizardFade {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

// .wizard-card-footer {
//   position: absolute;
//   width: 100%;
//   left: 0;
//   bottom: 2rem;
// }
</style>



// WEBPACK FOOTER //
// src/components/Wizard.vue